/**
 * Entry Form Component
 *
 * Form for creating new accomplishment entries.
 * Celebration-focused, low-friction design.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, Clock, Send, ChevronDown, Smile } from 'lucide-react';
import {
  useCreateEntry,
  type Category,
  validateContent,
  validateSatisfaction,
  validateTimeSpent,
  VALIDATION,
} from '@have-done/shared';
import { CategorySelector } from './CategorySelector';
import { SatisfactionRating } from './SatisfactionRating';
import { useToast } from './Toast';
import { useConfetti } from '../hooks/useConfetti';

const LAST_CATEGORY_KEY = 'have-done:last-category';
const DRAFT_KEY = 'have-done:entry-draft';
const DRAFT_DEBOUNCE_MS = 500;

interface DraftData {
  content: string;
  category: Category | null;
  satisfaction: number | null;
  timeSpent: string;
  isBigWin: boolean;
  showOptional: boolean;
  timestamp: number;
}

interface EntryFormProps {
  onSuccess?: () => void;
}

export function EntryForm({ onSuccess }: EntryFormProps) {
  // Load initial state from draft if available
  const getInitialState = useCallback(() => {
    try {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        const draft: DraftData = JSON.parse(savedDraft);
        // Only use draft if it's less than 24 hours old
        const oneDayMs = 24 * 60 * 60 * 1000;
        if (Date.now() - draft.timestamp < oneDayMs) {
          return draft;
        }
        // Clear stale draft
        localStorage.removeItem(DRAFT_KEY);
      }
    } catch {
      localStorage.removeItem(DRAFT_KEY);
    }
    // Fall back to last category only
    const savedCategory = localStorage.getItem(LAST_CATEGORY_KEY);
    return {
      content: '',
      category: savedCategory ? (savedCategory as Category) : null,
      satisfaction: null,
      timeSpent: '',
      isBigWin: false,
      showOptional: false,
    };
  }, []);

  const initialState = getInitialState();
  const [content, setContent] = useState(initialState.content);
  const [category, setCategory] = useState<Category | null>(initialState.category);
  const [satisfaction, setSatisfaction] = useState<number | null>(initialState.satisfaction);
  const [timeSpent, setTimeSpent] = useState<string>(initialState.timeSpent);
  const [isBigWin, setIsBigWin] = useState(initialState.isBigWin);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOptional, setShowOptional] = useState(initialState.showOptional);

  const createEntry = useCreateEntry();
  const { showSuccess, showError } = useToast();
  const { celebrateBigWin } = useConfetti();
  const draftTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the draft from localStorage
  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
    if (draftTimeoutRef.current) {
      clearTimeout(draftTimeoutRef.current);
      draftTimeoutRef.current = null;
    }
  }, []);

  // Save draft with debouncing
  useEffect(() => {
    // Don't save empty drafts
    if (!content.trim() && !satisfaction && !timeSpent && !isBigWin) {
      clearDraft();
      return;
    }

    // Clear any pending save
    if (draftTimeoutRef.current) {
      clearTimeout(draftTimeoutRef.current);
    }

    // Schedule new save
    draftTimeoutRef.current = setTimeout(() => {
      const draft: DraftData = {
        content,
        category,
        satisfaction,
        timeSpent,
        isBigWin,
        showOptional,
        timestamp: Date.now(),
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    }, DRAFT_DEBOUNCE_MS);

    return () => {
      if (draftTimeoutRef.current) {
        clearTimeout(draftTimeoutRef.current);
      }
    };
  }, [content, category, satisfaction, timeSpent, isBigWin, showOptional, clearDraft]);

  // Persist category selection
  useEffect(() => {
    if (category) {
      localStorage.setItem(LAST_CATEGORY_KEY, category);
    }
  }, [category]);

  const canSubmit = content.trim().length > 0 && category !== null && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !category) return;

    // Validate content
    const contentValidation = validateContent(content);
    if (!contentValidation.valid) {
      showError(contentValidation.error || 'Invalid content');
      return;
    }

    // Validate satisfaction if provided
    const satisfactionValidation = validateSatisfaction(satisfaction);
    if (!satisfactionValidation.valid) {
      showError(satisfactionValidation.error || 'Invalid satisfaction rating');
      return;
    }

    // Validate time spent if provided
    const timeSpentMinutes = timeSpent ? parseInt(timeSpent, 10) : undefined;
    const timeValidation = validateTimeSpent(timeSpentMinutes);
    if (!timeValidation.valid) {
      showError(timeValidation.error || 'Invalid time spent');
      return;
    }

    setIsSubmitting(true);
    try {
      await createEntry({
        content: content.trim(),
        category,
        satisfaction: satisfaction ?? undefined,
        timeSpentMinutes,
        isBigWin,
      });

      // Reset form (keep category for convenience)
      setContent('');
      setSatisfaction(null);
      setTimeSpent('');
      setIsBigWin(false);
      setShowOptional(false);

      // Clear the draft since submission succeeded
      clearDraft();

      // Celebrate big wins with confetti!
      if (isBigWin) {
        celebrateBigWin();
      }

      showSuccess(isBigWin ? '🎉 Big win logged!' : 'Accomplishment logged!');
      onSuccess?.();
    } catch (error) {
      console.error('Failed to create entry:', error);
      showError('Failed to save your accomplishment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Main input */}
      <div>
        <label htmlFor="content" className="sr-only">
          What did you accomplish?
        </label>
        <textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What did you accomplish?"
          disabled={isSubmitting}
          rows={3}
          maxLength={VALIDATION.content.maxLength}
          className="
            w-full rounded-lg border border-border-light bg-bg-primary px-4 py-3
            text-text-primary placeholder-text-tertiary
            resize-none
            transition-colors
            focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        />
      </div>

      {/* Category selector */}
      <div>
        <p className="mb-2 text-sm font-medium text-text-secondary">Category</p>
        <CategorySelector value={category} onChange={setCategory} disabled={isSubmitting} />
      </div>

      {/* Optional Details Section */}
      <div className="pt-1">
        {/* Toggle Button */}
        <button
          type="button"
          onClick={() => setShowOptional(!showOptional)}
          className="
            group flex items-center gap-2 w-full py-2
            text-sm font-medium text-text-secondary
            hover:text-text-primary transition-colors
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
            rounded-lg
          "
          aria-expanded={showOptional}
          aria-controls="optional-details"
        >
          <div className="flex items-center gap-2">
            <ChevronDown
              className={`
                h-4 w-4 text-text-tertiary
                transition-transform duration-200 ease-out
                group-hover:text-text-secondary
                ${showOptional ? 'rotate-180' : ''}
              `}
              aria-hidden="true"
            />
            <span>Additional details</span>
          </div>
          <div className="flex-1 h-px bg-border-light ml-2" aria-hidden="true" />
        </button>

        {/* Optional Fields Panel */}
        <div
          id="optional-details"
          className={`
            overflow-hidden transition-all duration-300 ease-out
            ${showOptional ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="pt-4 space-y-5">
            {/* Satisfaction Rating */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-text-secondary">
                <Smile className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                How did it feel?
              </label>
              <SatisfactionRating
                value={satisfaction}
                onChange={setSatisfaction}
                disabled={isSubmitting}
                showLabels
              />
            </div>

            {/* Time Spent & Big Win Row */}
            <div className="flex flex-wrap items-start gap-6">
              {/* Time spent */}
              <div className="space-y-2">
                <label
                  htmlFor="timeSpent"
                  className="flex items-center gap-2 text-sm font-medium text-text-secondary"
                >
                  <Clock className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                  Time spent
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    id="timeSpent"
                    value={timeSpent}
                    onChange={(e) => setTimeSpent(e.target.value)}
                    min="1"
                    max="1440"
                    placeholder="30"
                    disabled={isSubmitting}
                    className="
                      w-20 rounded-lg border border-border-light bg-bg-primary px-3 py-2
                      text-text-primary placeholder-text-tertiary text-center
                      transition-shadow transition-colors duration-150
                      focus:border-primary focus:outline-none focus:shadow-[0_0_0_3px_rgba(107,91,149,0.15)]
                      disabled:opacity-50 disabled:cursor-not-allowed
                      [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                    "
                  />
                  <span className="text-sm text-text-tertiary">minutes</span>
                </div>
              </div>

              {/* Big win toggle */}
              <div className="space-y-2">
                <span className="flex items-center gap-2 text-sm font-medium text-text-secondary">
                  <Sparkles
                    className={`h-4 w-4 transition-colors ${isBigWin ? 'text-accent' : 'text-text-tertiary'}`}
                    aria-hidden="true"
                  />
                  Big win?
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isBigWin}
                  onClick={() => setIsBigWin(!isBigWin)}
                  disabled={isSubmitting}
                  className={`
                    group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full
                    transition-all duration-200 ease-out
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${isBigWin
                      ? 'bg-gradient-to-r from-accent to-accent-light shadow-inner'
                      : 'bg-border hover:bg-border-dark'
                    }
                  `}
                >
                  <span
                    className={`
                      pointer-events-none inline-flex h-5 w-5 rounded-full items-center justify-center
                      bg-white shadow-md ring-0 transition-all duration-200 ease-out
                      ${isBigWin ? 'translate-x-6' : 'translate-x-1'}
                      ${isBigWin ? 'scale-100' : 'group-hover:scale-105'}
                    `}
                    style={{ marginTop: '4px' }}
                  >
                    {isBigWin && (
                      <Sparkles className="h-3 w-3 text-accent" aria-hidden="true" />
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit button */}
      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="
            inline-flex items-center justify-center gap-2
            rounded-lg bg-primary px-6 py-2.5
            text-sm font-medium text-white
            transition-colors
            hover:bg-primary-dark
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          <Send className="h-4 w-4" aria-hidden="true" />
          {isSubmitting ? 'Saving...' : 'Log accomplishment'}
        </button>
      </div>
    </form>
  );
}
